/** First-party cookies set on landing and read at Stripe Checkout. */
export const ADS_ATTRIBUTION_COOKIES = {
  gclid: "__cb_gclid",
  gbraid: "__cb_gbraid",
  wbraid: "__cb_wbraid",
  ttclid: "__cb_ttclid",
  gad_source: "__cb_gad_source",
  gad_campaignid: "__cb_gad_campaignid",
  utm_source: "__cb_utm_source",
  utm_medium: "__cb_utm_medium",
  utm_campaign: "__cb_utm_campaign",
  landing_path: "__cb_landing",
} as const;

export const ADS_ATTRIBUTION_MAX_AGE_SEC = 90 * 24 * 60 * 60;
export const ADS_ATTRIBUTION_VALUE_MAX = 500;

const OPENAI_COOKIES = {
  openai_ads_oppref: "__oppref",
  openai_ads_obref: "__obref",
} as const;

const TIKTOK_COOKIE = "_ttp";
const GOOGLE_GCL_AW = "_gcl_aw";
const GOOGLE_GCL_GB = "_gcl_gb";

const TOKEN = /^[\w.~+-]{1,500}$/;
const LANDING = /^\/[\w./?&=%~+-]{0,499}$/;
const OPAQUE = /^[^\s;,]{1,500}$/;

export type CheckoutAttribution = {
  openai_ads_oppref?: string;
  openai_ads_obref?: string;
  tiktok_ttclid?: string;
  tiktok_ttp?: string;
  tiktok_ip?: string;
  tiktok_user_agent?: string;
  tiktok_page_url?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  gad_source?: string;
  gad_campaignid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_path?: string;
};

export type CookieStore = {
  get: (name: string) => string | undefined;
  set: (name: string, value: string, maxAgeSec: number) => void;
};

const trimToMax = (value: string): string =>
  value.length > ADS_ATTRIBUTION_VALUE_MAX ? value.slice(0, ADS_ATTRIBUTION_VALUE_MAX) : value;

const sanitizeToken = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!TOKEN.test(trimmed)) return undefined;
  return trimmed;
};

const sanitizeOpaque = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = trimToMax(value.trim());
  if (!OPAQUE.test(trimmed)) return undefined;
  return trimmed;
};

const sanitizeLanding = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = trimToMax(value.trim());
  if (!LANDING.test(trimmed)) return undefined;
  return trimmed;
};

export const parseCookieHeader = (header: string | undefined): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!header) return out;

  for (const part of header.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;
    const name = part.slice(0, separator).trim();
    if (!name) continue;
    let value = part.slice(separator + 1).trim();
    try {
      value = decodeURIComponent(value);
    } catch {
      // keep raw
    }
    if (value.length === 0 || value.length > ADS_ATTRIBUTION_VALUE_MAX) continue;
    out[name] = value;
  }

  return out;
};

/** `_gcl_aw` / `_gcl_gb` values look like `GCL.<timestamp>.<id>`. */
export const parseGoogleLinkerCookie = (raw: string | undefined): string | undefined => {
  if (!raw) return undefined;
  const parts = raw.split(".");
  if (parts.length < 3 || parts[0] !== "GCL") return undefined;
  return sanitizeToken(parts.slice(2).join("."));
};

export const attributionFromCookies = (cookies: Record<string, string>): CheckoutAttribution => {
  const gclid =
    sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.gclid]) ??
    parseGoogleLinkerCookie(cookies[GOOGLE_GCL_AW]);
  const gbraid =
    sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.gbraid]) ??
    parseGoogleLinkerCookie(cookies[GOOGLE_GCL_GB]);

  const attribution: CheckoutAttribution = {
    openai_ads_oppref: sanitizeOpaque(cookies[OPENAI_COOKIES.openai_ads_oppref]),
    openai_ads_obref: sanitizeOpaque(cookies[OPENAI_COOKIES.openai_ads_obref]),
    tiktok_ttclid: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.ttclid]),
    tiktok_ttp: sanitizeOpaque(cookies[TIKTOK_COOKIE]),
    gclid,
    gbraid,
    wbraid: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.wbraid]),
    gad_source: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.gad_source]),
    gad_campaignid: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.gad_campaignid]),
    utm_source: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.utm_source]),
    utm_medium: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.utm_medium]),
    utm_campaign: sanitizeToken(cookies[ADS_ATTRIBUTION_COOKIES.utm_campaign]),
    landing_path: sanitizeLanding(cookies[ADS_ATTRIBUTION_COOKIES.landing_path]),
  };

  return Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => Boolean(value)),
  ) as CheckoutAttribution;
};

/** Click / campaign IDs we persist on Stripe when present. */
const STRIPE_SOURCE_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "landing_path",
  "openai_ads_oppref",
  "tiktok_ttclid",
] as const satisfies readonly (keyof CheckoutAttribution)[];

/** Pixel match keys — only if the user actually came from that network. */
const STRIPE_OPENAI_MATCH_KEYS = ["openai_ads_obref"] as const satisfies readonly (keyof CheckoutAttribution)[];
const STRIPE_TIKTOK_MATCH_KEYS = [
  "tiktok_ttp",
  "tiktok_ip",
  "tiktok_user_agent",
  "tiktok_page_url",
] as const satisfies readonly (keyof CheckoutAttribution)[];

/**
 * Stripe labels for later inspection / delayed CAPI. Drops pixel browser IDs
 * and request IP/UA unless there is a real click from that network.
 */
export const toStripeCheckoutMetadata = (
  attribution: CheckoutAttribution,
): Record<string, string> => {
  const metadata: Record<string, string> = {};
  const write = (key: keyof CheckoutAttribution) => {
    const value = attribution[key];
    if (!value) return;
    metadata[key] = trimToMax(value);
  };

  for (const key of STRIPE_SOURCE_KEYS) write(key);
  if (attribution.openai_ads_oppref) {
    for (const key of STRIPE_OPENAI_MATCH_KEYS) write(key);
  }
  if (attribution.tiktok_ttclid) {
    for (const key of STRIPE_TIKTOK_MATCH_KEYS) write(key);
  }
  return metadata;
};

const PAID_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "ttclid",
  "gad_source",
  "gad_campaignid",
] as const;
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign"] as const;

export const captureAdsAttribution = (input: {
  search: string;
  pathname: string;
  cookies: CookieStore;
}): void => {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(
      input.search.startsWith("?") ? input.search.slice(1) : input.search,
    );
  } catch {
    return;
  }

  const write = (cookie: string, value: string | undefined, overwrite: boolean) => {
    const sanitized =
      cookie === ADS_ATTRIBUTION_COOKIES.landing_path
        ? sanitizeLanding(value)
        : sanitizeToken(value);
    if (!sanitized) return;
    if (!overwrite && input.cookies.get(cookie)) return;
    input.cookies.set(cookie, sanitized, ADS_ATTRIBUTION_MAX_AGE_SEC);
  };

  let sawPaid = false;
  for (const param of PAID_PARAMS) {
    const value = params.get(param);
    if (!value) continue;
    sawPaid = true;
    write(ADS_ATTRIBUTION_COOKIES[param], value, true);
  }

  for (const param of UTM_PARAMS) {
    write(ADS_ATTRIBUTION_COOKIES[param], params.get(param) ?? undefined, true);
  }

  const path = input.pathname.startsWith("/") ? input.pathname : `/${input.pathname}`;
  if (sawPaid) {
    const query = params.toString();
    write(ADS_ATTRIBUTION_COOKIES.landing_path, query ? `${path}?${query}` : path, true);
  } else {
    write(ADS_ATTRIBUTION_COOKIES.landing_path, path, false);
  }
};
